import { useEffect, useState } from "react";

import {
  getSessionEntryStorageKey,
  resolveUserName,
  SESSION_ENTRY_CACHE_TTL_MS,
  type CachedAuthenticatedSession,
  type SessionEntryVariant,
  type SessionResponse
} from "./session-entry-shared";

type SessionEntryIslandProps = {
  loginUrl?: string;
  consoleUrl?: string;
  sessionApiUrl?: string;
  variant?: SessionEntryVariant;
};

type SessionViewState =
  | {
      status: "anonymous";
    }
  | {
      status: "authenticated";
      userName: string;
    };

const DEFAULT_LOGIN_URL = "/login";
const DEFAULT_CONSOLE_URL = "/console";
const sessionResponseCache = new Map<string, Promise<SessionResponse>>();

export function SessionEntryIsland(props: SessionEntryIslandProps) {
  const [viewState, setViewState] = useState<SessionViewState>(() => readCachedViewState(props.sessionApiUrl));
  const variant = props.variant === "hero" ? "hero" : "header";

  useEffect(() => {
    const sessionApiUrl = props.sessionApiUrl?.trim();
    if (!sessionApiUrl) {
      clearCachedSession(undefined);
      setViewState({ status: "anonymous" });
      return;
    }

    let active = true;

    void loadSessionResponse(sessionApiUrl)
      .then((payload) => {
        if (!active) {
          return;
        }
        if (!payload.authenticated) {
          clearCachedSession(sessionApiUrl);
          setViewState({ status: "anonymous" });
          return;
        }

        const userName = resolveUserName(payload.user);
        cacheAuthenticatedSession(sessionApiUrl, userName);
        setViewState({
          status: "authenticated",
          userName
        });
      })
      .catch((error: unknown) => {
        if (!active) {
          return;
        }
        clearCachedSession(sessionApiUrl);
        setViewState({ status: "anonymous" });
      });

    return () => {
      active = false;
    };
  }, [props.sessionApiUrl]);

  if (viewState.status === "authenticated") {
    if (variant === "hero") {
      return (
        <a
          className="inline-flex min-h-11 items-center justify-center rounded-full bg-brand-600 px-9 text-[15px] font-medium text-white shadow-[0_12px_24px_rgba(79,70,229,0.22)] transition-transform hover:-translate-y-0.5"
          href={props.consoleUrl || DEFAULT_CONSOLE_URL}
        >
          进入工作台
        </a>
      );
    }

    return (
      <div className="flex items-center gap-2 sm:gap-3">
        <span className="hidden rounded-full bg-slate-100 px-3 py-1 text-[13px] font-medium text-slate-600 sm:inline-flex">
          {viewState.userName}
        </span>
        <a
          className="inline-flex min-h-10 items-center justify-center whitespace-nowrap rounded-full border border-slate-200 bg-white px-4 text-[14px] font-medium text-slate-700 transition-transform hover:-translate-y-0.5 sm:px-5"
          href={props.consoleUrl || DEFAULT_CONSOLE_URL}
        >
          进入工作台
        </a>
      </div>
    );
  }

  return (
    <a
      className={
        variant === "hero"
          ? "inline-flex min-h-11 items-center justify-center rounded-full bg-brand-600 px-9 text-[15px] font-medium text-white shadow-[0_12px_24px_rgba(79,70,229,0.22)] transition-transform hover:-translate-y-0.5"
          : "inline-flex min-h-10 items-center justify-center whitespace-nowrap rounded-full bg-brand-600 px-4 text-[14px] font-medium text-white shadow-[0_12px_24px_rgba(79,70,229,0.22)] transition-transform hover:-translate-y-0.5 sm:px-6"
      }
      href={props.loginUrl || DEFAULT_LOGIN_URL}
    >
      {variant === "hero" ? "开始使用" : "登录/注册"}
    </a>
  );
}

function loadSessionResponse(sessionApiUrl: string): Promise<SessionResponse> {
  const cached = sessionResponseCache.get(sessionApiUrl);
  if (cached) {
    return cached;
  }

  const request = fetch(sessionApiUrl, {
    credentials: "include"
  })
    .then(async (response) => {
      if (response.status !== 200 && response.status !== 403) {
        return { authenticated: false } satisfies SessionResponse;
      }
      return (await response.json()) as SessionResponse;
    })
    .catch((error: unknown) => {
      sessionResponseCache.delete(sessionApiUrl);
      throw error;
    });

  sessionResponseCache.set(sessionApiUrl, request);
  return request;
}

function readCachedViewState(sessionApiUrl: string | undefined): SessionViewState {
  const cached = readCachedAuthenticatedSession(sessionApiUrl);
  if (!cached) {
    return { status: "anonymous" };
  }

  return {
    status: "authenticated",
    userName: cached.userName
  };
}

function readCachedAuthenticatedSession(
  sessionApiUrl: string | undefined
): CachedAuthenticatedSession | undefined {
  const normalizedSessionApiUrl = sessionApiUrl?.trim();
  if (!normalizedSessionApiUrl || typeof window === "undefined") {
    return undefined;
  }

  try {
    const raw = window.sessionStorage.getItem(getSessionEntryStorageKey(normalizedSessionApiUrl));
    if (!raw) {
      return undefined;
    }

    const parsed = JSON.parse(raw) as CachedAuthenticatedSession | null;
    if (!parsed || parsed.authenticated !== true || typeof parsed.expiresAt !== "number") {
      window.sessionStorage.removeItem(getSessionEntryStorageKey(normalizedSessionApiUrl));
      return undefined;
    }

    if (parsed.expiresAt <= Date.now()) {
      window.sessionStorage.removeItem(getSessionEntryStorageKey(normalizedSessionApiUrl));
      return undefined;
    }

    if (typeof parsed.userName !== "string" || !parsed.userName.trim()) {
      return {
        ...parsed,
        userName: "用户"
      };
    }

    return parsed;
  } catch {
    return undefined;
  }
}

function cacheAuthenticatedSession(sessionApiUrl: string, userName: string) {
  if (typeof window === "undefined") {
    return;
  }

  const snapshot: CachedAuthenticatedSession = {
    authenticated: true,
    userName: userName.trim() || "用户",
    expiresAt: Date.now() + SESSION_ENTRY_CACHE_TTL_MS
  };

  try {
    window.sessionStorage.setItem(getSessionEntryStorageKey(sessionApiUrl), JSON.stringify(snapshot));
  } catch {
    // Ignore storage write failures and fall back to network-only resolution.
  }
}

function clearCachedSession(sessionApiUrl: string | undefined) {
  const normalizedSessionApiUrl = sessionApiUrl?.trim();
  if (!normalizedSessionApiUrl || typeof window === "undefined") {
    return;
  }

  try {
    window.sessionStorage.removeItem(getSessionEntryStorageKey(normalizedSessionApiUrl));
  } catch {
    // Ignore storage cleanup failures.
  }
}
